import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import {
  BLOCKED_ORDER_COCKPIT_STATUSES,
  isBlockedOrderErrorType,
  type BlockedOrderDecision,
  type BlockedOrderSource,
  type ProvinceCode,
} from '@repo/constants'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Blocked orders (CT-31), over /credit/blocked-orders.
//
// As elsewhere in the credit hooks, no `res.ok` check: the hono client throws the parsed error body
// on any non-2xx before `res.json()` is reached, and that body carries the `clr.errors.<key>` a
// toast is translated from. Response types are inferred from the router.
// ─────────────────────────────────────────────────────────────────────────────────────────────

const toDecisionBatch = <
  T extends { decidedAt: string; orders: { createdAtSap: string | null }[] },
>(
  batch: T,
) => ({
  ...(batch as Omit<T, 'decidedAt' | 'orders'>),
  decidedAt: new Date(batch.decidedAt),
  orders: batch.orders.map((order) => ({
    ...(order as Omit<T['orders'][number], 'createdAtSap'>),
    createdAtSap: order.createdAtSap ? new Date(order.createdAtSap) : null,
  })),
})

async function fetchBlockedOrderReview(caseId: string) {
  const res = await getApi().credit['blocked-orders'].reviews[':id'].$get({
    param: { id: caseId },
  })
  const detail = await res.json()

  // Timestamps cross the wire as ISO strings; every consumer wants Dates.
  return {
    ...detail,
    raisedAt: new Date(detail.raisedAt),
    resolvedAt: detail.resolvedAt ? new Date(detail.resolvedAt) : null,
    cutoffAt: detail.cutoffAt ? new Date(detail.cutoffAt) : null,
    order: {
      ...detail.order,
      documentDate: detail.order.documentDate ? new Date(detail.order.documentDate) : null,
      createdAtSap: detail.order.createdAtSap ? new Date(detail.order.createdAtSap) : null,
    },
    lastDecisionBatch: detail.lastDecisionBatch ? toDecisionBatch(detail.lastDecisionBatch) : null,
  }
}

export const blockedOrderReviewQueryOptions = (caseId: string) =>
  queryOptions({
    queryKey: ['blockedOrderReview', caseId],
    queryFn: () => fetchBlockedOrderReview(caseId),
  })

export type BlockedOrderReviewDetail = Awaited<ReturnType<typeof fetchBlockedOrderReview>>

export function useBlockedOrderReview(caseId: string | null) {
  return useQuery({
    ...blockedOrderReviewQueryOptions(caseId ?? ''),
    enabled: caseId !== null,
  })
}

export function extractBlockedOrderErrorType(error: unknown): string | null {
  const issues = (error as { issues?: { message?: string }[] } | undefined)?.issues
  const message = issues?.[0]?.message
  return isBlockedOrderErrorType(message) ? message : null
}

async function fetchBlockedOrderResolutionSnapshot(caseId: string, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].reviews[':id']['resolution-snapshot'].$get(
    { param: { id: caseId } },
    { init: { signal } },
  )
  return await res.json()
}

export type BlockedOrderResolutionSnapshot = NonNullable<
  Awaited<ReturnType<typeof fetchBlockedOrderResolutionSnapshot>>
>

/**
 * The account as it stood when the order was released or rejected. Only fetched for a resolved
 * order — one still in its chain has no snapshot, and the request could only ever answer null.
 */
export function useBlockedOrderResolutionSnapshot(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['blockedOrderResolutionSnapshot', caseId],
    queryFn: ({ signal }) => fetchBlockedOrderResolutionSnapshot(caseId, signal),
    enabled,
    // Written once, never changed.
    staleTime: Infinity,
  })
}

/**
 * Whoever holds the exception chain's step sending the payer's blocked orders to the CEB bands
 * they would otherwise have had (CT-33). The server moves every pending exception order of the
 * payer, so the cockpit, the groups and the inbox all change. Rare and deliberate, so it gets its
 * own mutation rather than a flag on the decision.
 */
export function useReturnBlockedOrderToChain(caseId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: { comment: string }) => {
      const res = await getApi().credit['blocked-orders'].reviews[':id']['return-to-chain'].$post({
        param: { id: caseId },
        json: input,
      })
      return res.json()
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrders'] })
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      toast.success(
        t('blockedOrders.toasts.returnedToChain', { count: result?.movedCaseIds.length ?? 1 }),
      )
    },
    onError: (error) => {
      const errorType = extractBlockedOrderErrorType(error)
      toast.error(errorType ? t(`clr.errors.${errorType}`) : t('clr.toasts.error'))
    },
  })
}

/**
 * The way back (UAT 2026-10-29): whoever holds the normal chain's step on an exception payer's
 * order bringing the whole payer back to the exception chain. The server moves every pending order
 * of theirs in the normal chain, followers included.
 */
export function useReturnBlockedOrderToExceptionChain(caseId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: { comment: string }) => {
      const res = await getApi().credit['blocked-orders'].reviews[':id'][
        'return-to-exception-chain'
      ].$post({
        param: { id: caseId },
        json: input,
      })
      return res.json()
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrders'] })
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      toast.success(
        t('blockedOrders.toasts.returnedToExceptionChain', {
          count: result?.movedCaseIds.length ?? 1,
        }),
      )
    },
    onError: (error) => {
      const errorType = extractBlockedOrderErrorType(error)
      toast.error(errorType ? t(`clr.errors.${errorType}`) : t('clr.toasts.error'))
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The cockpit list (CT-30), over GET /credit/blocked-orders.
// ─────────────────────────────────────────────────────────────────────────────────────────────

// Deep links (emails, hand-typed URLs) can carry a scalar where the app serializes an array —
// normalize instead of crashing. Same guard as the CLR list.
const toStringArray = (value: unknown) =>
  value === undefined ? undefined : (Array.isArray(value) ? value : [value]).map(String)

/**
 * What one cockpit row is. `pending` — the default — is one row per payer and step: the orders
 * awaiting review, or awaiting approval — what still needs doing. `decision` is one row per
 * decision that released or rejected orders — what was finally decided, with where its SAP
 * hand-off stands. `none` is one row per order, pending or decided. Listed in the order the select
 * shows them. Adding a dimension is a value here, a grouped endpoint, and a table for its row.
 */
export const BLOCKED_ORDER_COCKPIT_GROUP_BYS = ['pending', 'decision', 'none'] as const
export const DEFAULT_BLOCKED_ORDER_COCKPIT_GROUP_BY: BlockedOrderCockpitGroupBy = 'pending'
export type BlockedOrderCockpitGroupBy = (typeof BLOCKED_ORDER_COCKPIT_GROUP_BYS)[number]

/** The half of the groups a grouped view asks the server for. */
export type BlockedOrderGroupKind = 'pending' | 'decided'
export const toBlockedOrderGroupKind = (
  groupBy: BlockedOrderCockpitGroupBy,
): BlockedOrderGroupKind => (groupBy === 'decision' ? 'decided' : 'pending')

/** `payerRecord` filter values: whether the payer has a customer row in Fuze. */
export const BLOCKED_ORDER_PAYER_RECORD_FILTERS = ['linked', 'missing'] as const
export type BlockedOrderPayerRecordFilter = (typeof BLOCKED_ORDER_PAYER_RECORD_FILTERS)[number]

/** `riskClassValidity` filter values: whether the payer's SAP risk class is one the ladder knows. */
export const BLOCKED_ORDER_RISK_CLASS_VALIDITY_FILTERS = ['valid', 'invalid'] as const
export type BlockedOrderRiskClassValidityFilter =
  (typeof BLOCKED_ORDER_RISK_CLASS_VALIDITY_FILTERS)[number]

/** `cebReadability` filter values: whether the CEB SAP sent for the payer could be parsed. */
export const BLOCKED_ORDER_CEB_READABILITY_FILTERS = ['readable', 'unreadable'] as const
export type BlockedOrderCebReadabilityFilter =
  (typeof BLOCKED_ORDER_CEB_READABILITY_FILTERS)[number]

export const blockedOrdersSearchSchema = z.object({
  groupBy: z.enum(BLOCKED_ORDER_COCKPIT_GROUP_BYS).optional(),
  keyword: z.string().optional(),
  payerErp: z.preprocess(toStringArray, z.array(z.string()).optional()),
  soldToErp: z.preprocess(toStringArray, z.array(z.string()).optional()),
  poNumber: z.string().optional(),
  province: z.preprocess(toStringArray, z.array(z.string()).optional()),
  status: z.preprocess(toStringArray, z.array(z.enum(BLOCKED_ORDER_COCKPIT_STATUSES)).optional()),
  doaOptionKey: z.preprocess(toStringArray, z.array(z.string()).optional()),
  caseType: z.preprocess(toStringArray, z.array(z.string()).optional()),
  payerRecord: z.preprocess(
    toStringArray,
    z.array(z.enum(BLOCKED_ORDER_PAYER_RECORD_FILTERS)).optional(),
  ),
  riskClassValidity: z.preprocess(
    toStringArray,
    z.array(z.enum(BLOCKED_ORDER_RISK_CLASS_VALIDITY_FILTERS)).optional(),
  ),
  cebReadability: z.preprocess(
    toStringArray,
    z.array(z.enum(BLOCKED_ORDER_CEB_READABILITY_FILTERS)).optional(),
  ),
  createdFrom: z.coerce.date().optional(),
  createdTo: z.coerce.date().optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
})
export type BlockedOrdersSearch = z.infer<typeof blockedOrdersSearchSchema>

type BlockedOrdersQueries = Omit<BlockedOrdersSearch, 'createdFrom' | 'createdTo' | 'groupBy'> & {
  createdFrom?: string
  createdTo?: string
  timezone: string
  page: number
  pageSize: number
}

async function getBlockedOrders(queries: BlockedOrdersQueries, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].$get(
    // `stringifyValues` widens every key to optional; `timezone` is always present, so restore it.
    { query: { ...stringifyValues(queries), timezone: queries.timezone } },
    { init: { signal } },
  )
  return await res.json()
}

export type BlockedOrderListRow = Awaited<ReturnType<typeof getBlockedOrders>>['rows'][number]

function toBlockedOrdersQueries(
  search: BlockedOrdersSearch & { pageSize: number },
): BlockedOrdersQueries {
  return {
    keyword: search.keyword,
    payerErp: search.payerErp,
    soldToErp: search.soldToErp,
    poNumber: search.poNumber,
    province: search.province,
    status: search.status,
    doaOptionKey: search.doaOptionKey,
    caseType: search.caseType,
    payerRecord: search.payerRecord,
    riskClassValidity: search.riskClassValidity,
    cebReadability: search.cebReadability,
    createdFrom: search.createdFrom ? formatDateOnly(search.createdFrom) : undefined,
    createdTo: search.createdTo ? formatDateOnly(search.createdTo) : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    page: search.page ?? 1,
    pageSize: search.pageSize,
  }
}

export function useBlockedOrders(
  search: BlockedOrdersSearch & { pageSize: number },
  options?: { enabled?: boolean },
) {
  const queries = toBlockedOrdersQueries(search)
  return useQuery({
    queryKey: ['blockedOrders', queries],
    queryFn: ({ signal }) => getBlockedOrders(queries, signal),
    enabled: options?.enabled,
  })
}

const toDate = (value: string | null) => (value ? new Date(value) : null)

type BlockedOrderGroupsQueries = BlockedOrdersQueries & { kind: BlockedOrderGroupKind }

async function getBlockedOrderGroups(queries: BlockedOrderGroupsQueries, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].groups.$get(
    { query: { ...stringifyValues(queries), timezone: queries.timezone, kind: queries.kind } },
    { init: { signal } },
  )
  const { groups, rowCount } = await res.json()
  return {
    rowCount,
    groups: groups.map((group) => ({
      ...group,
      cutoffAt: toDate(group.cutoffAt),
      decision: group.decision
        ? { ...group.decision, decidedAt: new Date(group.decision.decidedAt) }
        : null,
    })),
  }
}

export type BlockedOrderGroupRow = Awaited<
  ReturnType<typeof getBlockedOrderGroups>
>['groups'][number]

/**
 * The cockpit's grouped views, paged by group: pending groups — a payer's orders awaiting review,
 * or awaiting approval — or decided ones — each decision that released or rejected some.
 */
export function useBlockedOrderGroups(
  search: BlockedOrdersSearch & { pageSize: number; kind: BlockedOrderGroupsQueries['kind'] },
  options?: { enabled?: boolean },
) {
  const queries: BlockedOrderGroupsQueries = {
    ...toBlockedOrdersQueries(search),
    kind: search.kind,
  }
  return useQuery({
    // Under `blockedOrders` so everything that invalidates the cockpit refreshes this view too.
    queryKey: ['blockedOrders', 'groups', queries],
    queryFn: ({ signal }) => getBlockedOrderGroups(queries, signal),
    enabled: options?.enabled,
  })
}

async function fetchBlockedOrderGroup(key: string, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].groups[':key'].$get(
    { param: { key } },
    { init: { signal } },
  )
  const group = await res.json()
  return {
    ...group,
    cutoffAt: toDate(group.cutoffAt),
    decision: group.decision
      ? { ...group.decision, decidedAt: new Date(group.decision.decidedAt) }
      : null,
    orders: group.orders.map((order) => ({
      ...order,
      cutoffAt: toDate(order.cutoffAt),
      createdAtSap: toDate(order.createdAtSap),
    })),
    pendingAtDecision: group.pendingAtDecision.map((order) => ({
      ...order,
      cutoffAt: toDate(order.cutoffAt),
      createdAtSap: toDate(order.createdAtSap),
    })),
    approvals: group.approvals.map((approval) => ({
      ...approval,
      approvedAt: new Date(approval.approvedAt),
    })),
  }
}

export type BlockedOrderGroupDetail = Awaited<ReturnType<typeof fetchBlockedOrderGroup>>

/** One cockpit group's page. */
export function useBlockedOrderGroup(key: string) {
  return useQuery({
    queryKey: ['blockedOrders', 'group', key],
    queryFn: ({ signal }) => fetchBlockedOrderGroup(key, signal),
  })
}

async function fetchBlockedOrderGroupSapPayloads(key: string, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].groups[':key']['sap-payloads'].$get(
    { param: { key } },
    { init: { signal } },
  )
  return await res.json()
}

export type BlockedOrderSapPayload = Awaited<
  ReturnType<typeof fetchBlockedOrderGroupSapPayloads>
>['payloads'][number]

/**
 * What a decided group's orders went to SAP as, one IDOC per order, rebuilt from the snapshot.
 * Fetched only once the viewer opens it: the page does not need it to render.
 */
export function useBlockedOrderGroupSapPayloads(key: string, options: { enabled: boolean }) {
  return useQuery({
    queryKey: ['blockedOrders', 'group', key, 'sapPayloads'],
    queryFn: ({ signal }) => fetchBlockedOrderGroupSapPayloads(key, signal),
    enabled: options.enabled,
  })
}

/**
 * A payer's last release or rejection, for the customer page — the same card the inbox shows
 * under a pending order. Null when the payer never had one.
 */
export function useBlockedOrderPayerLastDecision(erp: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['blockedOrders', 'payerLastDecision', erp],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['blocked-orders'].payers[':erp']['last-decision'].$get(
        { param: { erp } },
        { init: { signal } },
      )
      const batch = await res.json()
      return batch ? toDecisionBatch(batch) : null
    },
    enabled: options?.enabled,
  })
}

/** Orders a payer had released or rejected together, as the order detail shows the last of them. */
export type BlockedOrderDecisionBatch = NonNullable<BlockedOrderReviewDetail['lastDecisionBatch']>

/**
 * One decision and one comment over several blocked orders. The endpoint answers per order, so a
 * batch can partly land: the toast says how many moved and how many were skipped, and a skipped
 * order stays on the payer page as it was.
 */
export function useBlockedOrderBatchDecision() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: {
      /** Each order, with the band the user saw it on, so a band that moved since is refused. */
      orders: { caseId: string; doaOptionKey: string | null }[]
      action: BlockedOrderDecision
      comment: string
    }) => {
      const res = await getApi().credit['blocked-orders'].reviews.actions.$post({ json: input })
      return await res.json()
    },
    onSuccess: ({ outcomes }) => {
      queryClient.invalidateQueries({ queryKey: ['blockedOrders'] })
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrderResolutionSnapshot'] })

      const appliedCount = outcomes.filter((outcome) => outcome.isApplied).length
      const skippedCount = outcomes.length - appliedCount
      if (appliedCount > 0) {
        toast.success(t('blockedOrders.batch.toasts.applied', { count: appliedCount }))
      }
      if (skippedCount > 0) {
        toast.warning(t('blockedOrders.batch.toasts.skipped', { count: skippedCount }))
      }
    },
    onError: () => toast.error(t('clr.toasts.error')),
  })
}

/**
 * TEMPORARY — the cockpit's "Create test orders" button, until CT-34's IDOC brings real orders.
 * The new records land in the inbox as well as the cockpit, and the payer's other open orders take
 * the entered CEB, so everything that reads blocked orders moves.
 */
export function useCreateTestBlockedOrders() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: {
      count: number
      payerErp: string
      province: ProvinceCode
      amount: number
      variance: number
      ceb: number
    }) => {
      const res = await getApi().credit['blocked-orders']['test-orders'].$post({ json: input })
      return await res.json()
    },
    onSuccess: ({ createdOrders }) => {
      queryClient.invalidateQueries({ queryKey: ['blockedOrders'] })
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview'] })
      toast.success(t('blockedOrders.testOrders.created', { count: createdOrders }))
    },
    onError: () => toast.error(t('clr.toasts.error')),
  })
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The caller's source: SAP's live cases, or the cockpit's test orders. Per user.
// ─────────────────────────────────────────────────────────────────────────────────────────────

export const blockedOrderSourceQueryKey = ['blockedOrders', 'source'] as const

/** Which source the viewer works against, and whether this environment can serve it. */
/**
 * The cockpit's other banner: payers of blocked orders with no customer row in Fuze. Polled with
 * the SAP banner, since the customer import can link a payer at any time.
 */
export function useBlockedOrderUnlinkedPayers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['blockedOrders', 'unlinkedPayers'],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['blocked-orders']['unlinked-payers'].$get(
        {},
        { init: { signal } },
      )
      return await res.json()
    },
    refetchInterval: 60_000,
    enabled: options?.enabled,
  })
}

/**
 * The cockpit's third banner: pending orders whose payer's risk class routed them to the
 * invalid-risk-class line. Polled like the others, since SAP rewrites the cases every few minutes.
 */
export function useBlockedOrderInvalidRiskClass(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['blockedOrders', 'invalidRiskClass'],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['blocked-orders']['invalid-risk-class'].$get(
        {},
        { init: { signal } },
      )
      return await res.json()
    },
    refetchInterval: 60_000,
    enabled: options?.enabled,
  })
}

/**
 * The cockpit's fourth banner: pending orders whose payer's CEB SAP sent but Fuze cannot parse —
 * held in the normal chain with nobody to act on them. Polled like the others.
 */
export function useBlockedOrderUnreadableCeb(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['blockedOrders', 'unreadableCeb'],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['blocked-orders']['unreadable-ceb'].$get(
        {},
        { init: { signal } },
      )
      return await res.json()
    },
    refetchInterval: 60_000,
    enabled: options?.enabled,
  })
}

export function useBlockedOrderSource() {
  return useQuery({
    queryKey: blockedOrderSourceQueryKey,
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['blocked-orders'].source.$get({}, { init: { signal } })
      return await res.json()
    },
  })
}

/**
 * Switches the viewer's source. Everything that reads blocked orders reads it from the server per
 * request, so the whole cache is dropped rather than keyed by source: nothing from one source may
 * linger on screen under the other.
 */
export function useSetBlockedOrderSource() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (source: BlockedOrderSource) => {
      const res = await getApi().credit['blocked-orders'].source.$put({ json: { source } })
      return await res.json()
    },
    onSuccess: (state) => {
      queryClient.setQueryData(blockedOrderSourceQueryKey, state)
      queryClient.removeQueries({
        predicate: ({ queryKey }) => queryKey[0] === 'blockedOrders' && queryKey[1] !== 'source',
      })
      for (const key of ['blockedOrderReview', 'blockedOrderResolutionSnapshot']) {
        queryClient.removeQueries({ queryKey: [key] })
      }
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      toast.success(t(`blockedOrders.source.switched.${state.source}`))
    },
    onError: () => toast.error(t('clr.toasts.error')),
  })
}
